import React from "react";
import {useAppSelector} from "@nextlot/core/webcast/hooks";
import {AuctionsManager} from "@nextlot/core/webcast";
import NumberDisplay from "../NumberDisplay";
import classNames from "classnames";
import {Bid, OnlineBid} from "@nextlot/core/webcast/data/data_types_definitions";
import {TaggedLogger} from "@nextlot/core/utilities";


const _logger = TaggedLogger.get('ClerkPlaceFloorAndOnlineBidButtonsStack');



/**
 * @return state [Array(3)]
 *      visibleState:-1|0|1
 *          0=not_visible, -1=visible_disabled, 1=visible_enabled
 *      amountCents?:number
 *          when visibleState=1, amount of cents to place as bid
 *      raiseToAmountCents?:number
 *          when visibleState=1 and amountCents=0, if the button will raise up the bid to the secondBid maximum
 */
function calculatePlaceOnlineBidState(askingBidValue:number, highestBid:Bid, firstOnlineBid:OnlineBid|null, secondOnlineBid:OnlineBid|null):[visibleState:-1|0|1, amountCents?:number, raiseToAmountCents?:number]  {
    if (!firstOnlineBid) {
        // no online bid
        return [0];
    }

    if (!askingBidValue) {
        // no asking bid, waiting for clerk to set increment
        return [-1];
    }

    const onlineMinBidAmountCents =  (firstOnlineBid.isSecret && secondOnlineBid) ? secondOnlineBid.maximumValue : firstOnlineBid.value;

    if (askingBidValue <= onlineMinBidAmountCents) {
        // always match the asking bid when possible

        if (!highestBid) {
            // is this the first bid? we can't raise, we must match asking
            return [1, askingBidValue];
        }
        else if (firstOnlineBid.userId === highestBid?.userId) {
            // it's not the first bid, must avoid out-bidding thyself
            return [-1];
        }
        else if (secondOnlineBid) {
            // after the first bid, always raise the bid up-to the second bidder (if exists)
            return [1, 0, onlineMinBidAmountCents];
        }
        else {
            // when no 2nd bidder, then match the asking bid
            return [1, askingBidValue];
        }
    }
    else if (askingBidValue <= firstOnlineBid.maximumValue) {
        // asking bid is over the min bid, check if we can bid towards the maximum
        if (firstOnlineBid.userId === highestBid?.userId) {
            // must avoid out-bidding thyself
            return [-1];
        }
        else {
            return [1, askingBidValue];
        }
    }
    else {
        // asking is over the maximum, we're outbid, no more bidding
        return [-1];
    }
}


export function ClerkPlaceFloorAndOnlineBidButtonsStack() {

    const currentRoundId = useAppSelector((state) => state.activeWebcast.webcastCurrentRoundBidding.roundId);
    const firstOnlineBid = useAppSelector((state) => state.activeWebcast.webcastCurrentRoundBidding.firstOnlineBid);
    const secondOnlineBid = useAppSelector((state) => state.activeWebcast.webcastCurrentRoundBidding.secondOnlineBid);
    const askingBidValue = useAppSelector((state) => state.activeWebcast.webcastCurrentRoundBidding.askingBidValue);
    const highestBid = useAppSelector((state) => state.activeWebcast.webcastCurrentRoundBidding.highestBid);


    const handleClick_FloorBidButton = () => {
        AuctionsManager.currentWebcastAuctionHandler.controller.sendBidPlace(currentRoundId, askingBidValue);
    };


    const [placeOnlineBidButtonVisibleState, placeOnlineBidCents, placeOnlineBidRaiseToAmountCents] = calculatePlaceOnlineBidState(askingBidValue, highestBid, firstOnlineBid, secondOnlineBid);

    const handleClick_PreBid = () => {
        AuctionsManager.currentWebcastAuctionHandler.controller.sendPreBidPlace(currentRoundId, firstOnlineBid.userId, placeOnlineBidCents);
    };


    return (
        <div className="pt-3 vstack gap-3" style={{ maxWidth: '11.3rem' }}>

            <button onClick={handleClick_FloorBidButton}
                    disabled={!askingBidValue}
                    className="ms-4 btn btn-lg btn-primary"
                    type="button">Floor Bid</button>

            {
                (placeOnlineBidButtonVisibleState !== 0)
                &&
                <div className="d-flex flex-column align-items-stretch">
                    <button onClick={handleClick_PreBid}
                            disabled={placeOnlineBidButtonVisibleState < 0}
                            className={classNames('btn btn-lg btn-block', placeOnlineBidButtonVisibleState < 0 ? 'btn-outline-info' : 'btn-danger')}
                            type="button">PreBid</button>
                    <div className="d-flex justify-content-center">Max Bid up to {NumberDisplay.formatValueCents(firstOnlineBid.maximumValue)}</div>
                    {
                        (placeOnlineBidButtonVisibleState > 0)
                        &&
                        (
                            (placeOnlineBidCents === 0 && placeOnlineBidRaiseToAmountCents)
                                ? <div className="text-center">bid up to <strong>{NumberDisplay.formatValueCents(placeOnlineBidRaiseToAmountCents)}</strong></div>
                                : <div className="text-center">for <strong>{NumberDisplay.formatValueCents(placeOnlineBidCents)}</strong></div>
                        )
                    }

                </div>
            }
        </div>
    )
}

